import {
  PermissionCode,
  createBusinessPartnerAddressRequestSchema,
  createBusinessPartnerContactRequestSchema,
  masterDataDateSchema,
  masterDataDuplicateDetailsSchema,
  patchBusinessPartnerAddressRequestSchema,
  patchBusinessPartnerContactRequestSchema,
  type BusinessPartnerDetail,
  type MasterDataDuplicateCandidate,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, type FieldPath, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';
import {
  useAddBusinessPartnerAddress,
  useAddBusinessPartnerContact,
  useAddBusinessPartnerRole,
  useBusinessPartner,
  usePatchBusinessPartner,
  usePatchBusinessPartnerAddress,
  usePatchBusinessPartnerContact,
  usePatchBusinessPartnerRole,
} from '@/platform/features/master-data/use-master-data';
import { masterDataPartnerPath } from '@/platform/lib/master-data-paths';
import {
  PartnerBankSection,
  PartnerTaxSection,
} from '@/platform/pages/master-data/partner-bank-tax-forms';
import { PartnerSettlementSection } from '@/platform/pages/master-data/partner-settlement-section';

const ROLE_LABEL = { supplier: 'Lieferant', customer: 'Kunde' } as const;

const nameSuccessorSchema = z.object({
  legalName: z.string().trim().min(1).max(200),
  tradeName: z.string().trim().min(1).max(200).nullable().optional(),
  effectiveFrom: masterDataDateSchema,
});
type NameSuccessor = z.infer<typeof nameSuccessorSchema>;

const addressSuccessorSchema = patchBusinessPartnerAddressRequestSchema.extend({
  effectiveFrom: masterDataDateSchema,
});
const contactSuccessorSchema = patchBusinessPartnerContactRequestSchema.extend({
  name: z.string().trim().min(1).max(200),
  effectiveFrom: masterDataDateSchema,
});

const PURPOSE_LABEL: Record<BusinessPartnerDetail['addresses'][number]['purpose'], string> = {
  registered: 'Sitz',
  billing: 'Rechnung',
  shipping: 'Versand',
  other: 'Sonstiges',
};

function blankToNull(value: string | null): string | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function validityText(validFrom: string | null, validTo: string | null): string {
  const from = validFrom ? `ab ${validFrom}` : '';
  const to = validTo ? `bis ${validTo}` : 'offen';
  return [from, to].filter(Boolean).join(' · ');
}

function toastFailure(error: unknown, fallback: string) {
  toast.error(error instanceof ApiRequestError ? error.message : fallback);
}

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }
  return (
    <p role="alert" className="text-sm text-destructive">
      {message}
    </p>
  );
}

export function PartnerDetailPage() {
  const { partnerId = '' } = useParams();
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.MASTER_DATA_MANAGE);
  const canWriteSensitive =
    canWrite && hasPermission(me.data, PermissionCode.MASTER_DATA_SENSITIVE_MANAGE);
  const partner = useBusinessPartner(partnerId);
  const patch = usePatchBusinessPartner(partnerId);
  const [addressSuccessorId, setAddressSuccessorId] = useState<string | null>(null);
  const [contactSuccessorId, setContactSuccessorId] = useState<string | null>(null);
  const [roleSuccessorId, setRoleSuccessorId] = useState<string | null>(null);
  const row = partner.data;
  const openName = row?.names.find((name) => name.validTo == null) ?? row?.names[0];

  async function setActive(isActive: boolean) {
    try {
      await patch.mutateAsync({ isActive });
      toast.success(isActive ? 'Geschäftspartner aktiviert.' : 'Geschäftspartner inaktiviert.');
    } catch (error) {
      toastFailure(
        error,
        isActive
          ? 'Der Geschäftspartner konnte nicht aktiviert werden.'
          : 'Der Geschäftspartner konnte nicht inaktiviert werden.',
      );
    }
  }

  return (
    <MasterDataListState
      pending={partner.isPending}
      pendingLabel="Geschäftspartner wird geladen …"
      error={partner.isError ? partner.error : null}
      errorLabel="Der Geschäftspartner konnte nicht geladen werden."
      empty={!partner.isPending && !partner.isError && !row}
      emptyLabel="Der Geschäftspartner wurde nicht gefunden."
    >
      {row ? (
        <section className="space-y-8">
          <PlatformPageHeader
            title={openName?.legalName ?? row.partnerNumber}
            description={`${row.partnerNumber}${row.inactiveLabel ? ` · ${row.inactiveLabel}` : ''}`}
            action={
              canWrite ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={patch.isPending}
                  onClick={() => void setActive(!row.isActive)}
                >
                  {row.isActive ? 'Inaktivieren' : 'Aktivieren'}
                </Button>
              ) : undefined
            }
          />
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Rollen</h2>
            {row.roles.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Rolle.</p>
            ) : (
              <ul className="space-y-3 text-sm">
                {row.roles.map((role) => (
                  <li key={role.id} className="space-y-2">
                    <p>
                      {ROLE_LABEL[role.role]}
                      {role.isActive ? '' : ' · inaktiv'}
                      {` · ${validityText(role.validFrom, role.validTo)}`}
                    </p>
                    {canWrite && role.validTo == null && roleSuccessorId !== role.id ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setRoleSuccessorId(role.id)}
                      >
                        Rolle fortschreiben
                      </Button>
                    ) : null}
                    {roleSuccessorId === role.id ? (
                      <RoleSuccessorForm
                        partnerId={partnerId}
                        roleId={role.id}
                        onDone={() => setRoleSuccessorId(null)}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {canWrite &&
            !row.roles.some((role) => role.role === 'customer' && role.validTo == null) ? (
              <CustomerRoleForm partnerId={partnerId} />
            ) : null}
          </section>
          <section className="max-w-xl space-y-3">
            <h2 className="text-base font-semibold">Name</h2>
            <ul className="space-y-2 text-sm">
              {row.names.map((name) => (
                <li key={name.id}>
                  {name.legalName}
                  {name.tradeName ? ` · ${name.tradeName}` : ''}
                  {` · ${validityText(name.validFrom, name.validTo)}`}
                </li>
              ))}
            </ul>
            {canWrite && openName ? (
              <NameSuccessorForm
                partnerId={partnerId}
                legalName={openName.legalName}
                tradeName={openName.tradeName}
              />
            ) : null}
          </section>
          <section className="max-w-xl space-y-3">
            <h2 className="text-base font-semibold">Adressen</h2>
            {row.addresses.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Adresse.</p>
            ) : (
              <ul className="space-y-3 text-sm">
                {row.addresses.map((address) => (
                  <li key={address.id} className="space-y-2">
                    <p>
                      {PURPOSE_LABEL[address.purpose]}
                      {address.isPrimary ? ' · Primär' : ''}
                      {` · ${address.line1}, ${address.postalCode} ${address.city}`}
                      {` · ${validityText(address.validFrom, address.validTo)}`}
                      {address.validTo ? ' · historisch' : ''}
                    </p>
                    {canWrite && address.validTo == null && addressSuccessorId !== address.id ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setAddressSuccessorId(address.id)}
                      >
                        Fortschreiben
                      </Button>
                    ) : null}
                    {addressSuccessorId === address.id ? (
                      <AddressSuccessorForm
                        partnerId={partnerId}
                        address={address}
                        onDone={() => setAddressSuccessorId(null)}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {canWrite && addressSuccessorId == null ? (
              <AddressCreateForm partnerId={partnerId} />
            ) : null}
          </section>
          <section className="max-w-xl space-y-3">
            <h2 className="text-base font-semibold">Kontakte</h2>
            {row.contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch kein Ansprechpartner.</p>
            ) : (
              <ul className="space-y-3 text-sm">
                {row.contacts.map((contact) => (
                  <li key={contact.id} className="space-y-2">
                    <p>
                      {contact.name}
                      {contact.functionTitle ? ` · ${contact.functionTitle}` : ''}
                      {contact.email ? ` · ${contact.email}` : ''}
                      {contact.phone ? ` · ${contact.phone}` : ''}
                      {contact.isPrimary ? ' · Primär' : ''}
                      {` · ${validityText(contact.validFrom, contact.validTo)}`}
                    </p>
                    {canWrite && contact.validTo == null && contactSuccessorId !== contact.id ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setContactSuccessorId(contact.id)}
                      >
                        Fortschreiben
                      </Button>
                    ) : null}
                    {contactSuccessorId === contact.id ? (
                      <ContactSuccessorForm
                        partnerId={partnerId}
                        contact={contact}
                        onDone={() => setContactSuccessorId(null)}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {canWrite && contactSuccessorId == null ? (
              <ContactCreateForm partnerId={partnerId} />
            ) : null}
          </section>
          <PartnerSettlementSection
            partnerId={partnerId}
            settlements={row.settlements}
            canWrite={canWrite}
          />
          <PartnerBankSection
            partnerId={partnerId}
            accounts={row.bankAccounts}
            canWrite={canWriteSensitive}
          />
          <PartnerTaxSection
            partnerId={partnerId}
            profiles={row.taxProfiles}
            canWrite={canWriteSensitive}
          />
        </section>
      ) : null}
    </MasterDataListState>
  );
}

function CustomerRoleForm({ partnerId }: { partnerId: string }) {
  const add = useAddBusinessPartnerRole(partnerId);
  const form = useForm({
    resolver: zodResolver(z.object({ validFrom: masterDataDateSchema })),
    defaultValues: { validFrom: '' },
  });

  async function onValid(values: { validFrom: string }) {
    try {
      await add.mutateAsync({ role: 'customer', validFrom: values.validFrom });
      toast.success('Die Kundenrolle wurde angelegt.');
      form.reset();
    } catch (error) {
      toastFailure(error, 'Die Kundenrolle konnte nicht angelegt werden.');
    }
  }

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={form.handleSubmit(onValid)}
      noValidate
    >
      <div className="space-y-2">
        <Label htmlFor="customer-valid-from">Kunde gültig ab</Label>
        <Input id="customer-valid-from" placeholder="JJJJ-MM-TT" {...form.register('validFrom')} />
      </div>
      <Button type="submit" disabled={add.isPending}>
        Kunde hinzufügen
      </Button>
    </form>
  );
}

function RoleSuccessorForm({
  partnerId,
  roleId,
  onDone,
}: {
  partnerId: string;
  roleId: string;
  onDone: () => void;
}) {
  const copy = useVoiceCopy();
  const patch = usePatchBusinessPartnerRole(partnerId);
  const form = useForm({
    resolver: zodResolver(z.object({ effectiveFrom: masterDataDateSchema })),
    defaultValues: { effectiveFrom: '' },
  });

  async function onValid(values: { effectiveFrom: string }) {
    try {
      await patch.mutateAsync({ roleId, body: values });
      toast.success('Rolle fortgeschrieben.');
      onDone();
    } catch (error) {
      toastFailure(error, 'Die Rolle konnte nicht fortgeschrieben werden.');
    }
  }

  const dateError = form.formState.errors.effectiveFrom;

  return (
    <form className="max-w-xl space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor={`role-${roleId}-from`}>Gültig ab</Label>
        <Input
          id={`role-${roleId}-from`}
          placeholder="JJJJ-MM-TT"
          {...form.register('effectiveFrom')}
          aria-invalid={dateError ? true : undefined}
        />
        <FieldError message={dateError ? copy('platform.form.date') : undefined} />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={patch.isPending}>
          Rolle speichern
        </Button>
        <Button type="button" variant="outline" onClick={onDone}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function NameSuccessorForm({
  partnerId,
  legalName,
  tradeName,
}: {
  partnerId: string;
  legalName: string;
  tradeName: string | null;
}) {
  const copy = useVoiceCopy();
  const patch = usePatchBusinessPartner(partnerId);
  const [candidates, setCandidates] = useState<MasterDataDuplicateCandidate[]>([]);
  const form = useForm<NameSuccessor>({
    resolver: zodResolver(nameSuccessorSchema),
    defaultValues: { legalName, tradeName, effectiveFrom: '' },
  });

  async function onValid(values: NameSuccessor) {
    setCandidates([]);
    try {
      await patch.mutateAsync(values);
      toast.success('Name fortgeschrieben.');
      form.reset({ ...values, effectiveFrom: '' });
    } catch (error) {
      const found = duplicateCandidates(error);
      if (found) {
        setCandidates(found);
        return;
      }
      toastFailure(error, 'Der Name konnte nicht fortgeschrieben werden.');
    }
  }

  async function confirmDuplicate() {
    const valid = await form.trigger();
    if (!valid) {
      return;
    }
    try {
      await patch.mutateAsync({ ...form.getValues(), acknowledgeDuplicate: true });
      toast.success('Name fortgeschrieben.');
      setCandidates([]);
      form.reset({ ...form.getValues(), effectiveFrom: '' });
    } catch (error) {
      toastFailure(error, 'Der Name konnte nicht fortgeschrieben werden.');
    }
  }

  const nameError = form.formState.errors.legalName;
  const dateError = form.formState.errors.effectiveFrom;

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="name-legal">Rechtlicher Name</Label>
        <Input
          id="name-legal"
          {...form.register('legalName')}
          aria-invalid={nameError ? true : undefined}
        />
        <FieldError message={nameError ? copy('platform.form.legal-name') : undefined} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="name-trade">Handelsname</Label>
        <Input id="name-trade" {...form.register('tradeName', { setValueAs: blankToNull })} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="name-from">Gültig ab</Label>
        <Input
          id="name-from"
          placeholder="JJJJ-MM-TT"
          {...form.register('effectiveFrom')}
          aria-invalid={dateError ? true : undefined}
        />
        <FieldError message={dateError ? copy('platform.form.date') : undefined} />
      </div>
      <Button type="submit" disabled={patch.isPending}>
        Namen fortschreiben
      </Button>
      {candidates.length > 0 ? (
        <div role="alert" className="space-y-3 rounded-md border p-3 text-sm">
          <p>Ein Geschäftspartner mit diesem rechtlichen Namen ist schon vorhanden.</p>
          <ul className="space-y-1">
            {candidates.map((candidate) => (
              <li key={candidate.id}>
                <Link className="underline" to={masterDataPartnerPath(candidate.id)}>
                  {candidate.partnerNumber}
                </Link>
                {` · ${candidate.legalName}`}
              </li>
            ))}
          </ul>
          <Button type="button" disabled={patch.isPending} onClick={confirmDuplicate}>
            Trotzdem fortschreiben
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function duplicateCandidates(error: unknown): MasterDataDuplicateCandidate[] | null {
  if (!(error instanceof ApiRequestError) || error.error.code !== 'MASTER_DATA_DUPLICATE') {
    return null;
  }
  const parsed = masterDataDuplicateDetailsSchema.safeParse(error.error.details);
  return parsed.success && parsed.data.candidates.length > 0 ? parsed.data.candidates : null;
}

function AddressCreateForm({ partnerId }: { partnerId: string }) {
  const create = useAddBusinessPartnerAddress(partnerId);
  const form = useForm({
    resolver: zodResolver(createBusinessPartnerAddressRequestSchema),
    defaultValues: {
      purpose: 'billing' as const,
      isPrimary: false,
      line1: '',
      line2: null,
      postalCode: '',
      city: '',
      country: 'DE',
      validFrom: '',
      validTo: null,
    },
  });

  return (
    <AddressFields
      form={form}
      idPrefix="address-new"
      submitLabel="Adresse anlegen"
      pending={create.isPending}
      dateField="validFrom"
      onValid={async (values) => {
        try {
          await create.mutateAsync({ ...values, isPrimary: values.isPrimary ?? false });
          toast.success('Adresse angelegt.');
          form.reset();
        } catch (error) {
          toastFailure(error, 'Die Adresse konnte nicht angelegt werden.');
        }
      }}
    />
  );
}

function AddressSuccessorForm({
  partnerId,
  address,
  onDone,
}: {
  partnerId: string;
  address: BusinessPartnerDetail['addresses'][number];
  onDone: () => void;
}) {
  const patch = usePatchBusinessPartnerAddress(partnerId);
  const form = useForm({
    resolver: zodResolver(addressSuccessorSchema),
    defaultValues: {
      purpose: address.purpose,
      isPrimary: address.isPrimary,
      line1: address.line1,
      line2: address.line2,
      postalCode: address.postalCode,
      city: address.city,
      country: address.country,
      effectiveFrom: '',
    },
  });

  return (
    <AddressFields
      form={form}
      idPrefix={`address-${address.id}`}
      submitLabel="Adresse fortschreiben"
      pending={patch.isPending}
      dateField="effectiveFrom"
      onCancel={onDone}
      onValid={async (values) => {
        try {
          await patch.mutateAsync({
            addressId: address.id,
            body: { ...values, isPrimary: values.isPrimary ?? false },
          });
          toast.success('Adresse fortgeschrieben.');
          onDone();
        } catch (error) {
          toastFailure(error, 'Die Adresse konnte nicht fortgeschrieben werden.');
        }
      }}
    />
  );
}

function AddressFields<T extends FieldValues>({
  form,
  idPrefix,
  submitLabel,
  pending,
  onValid,
  onCancel,
  dateField,
}: {
  form: UseFormReturn<T>;
  idPrefix: string;
  submitLabel: string;
  pending: boolean;
  onValid: (values: T) => Promise<void>;
  onCancel?: () => void;
  dateField: FieldPath<T>;
}) {
  const copy = useVoiceCopy();
  const errors = form.formState.errors;
  const dateError = errors[dateField];

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-purpose`}>Zweck</Label>
        <Select
          value={String(form.watch('purpose' as FieldPath<T>) ?? '') || undefined}
          onValueChange={(value) => form.setValue('purpose' as FieldPath<T>, value as never)}
        >
          <SelectTrigger id={`${idPrefix}-purpose`} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PURPOSE_LABEL).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <input
          id={`${idPrefix}-primary`}
          type="checkbox"
          {...form.register('isPrimary' as FieldPath<T>)}
        />
        <Label htmlFor={`${idPrefix}-primary`}>Primäradresse</Label>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-line1`}>Straße</Label>
        <Input
          id={`${idPrefix}-line1`}
          {...form.register('line1' as FieldPath<T>)}
          aria-invalid={errors.line1 ? true : undefined}
        />
        <FieldError message={errors.line1 ? copy('platform.form.street') : undefined} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-postal`}>Postleitzahl</Label>
        <Input
          id={`${idPrefix}-postal`}
          {...form.register('postalCode' as FieldPath<T>)}
          aria-invalid={errors.postalCode ? true : undefined}
        />
        <FieldError message={errors.postalCode ? copy('platform.form.postal-code') : undefined} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-city`}>Ort</Label>
        <Input
          id={`${idPrefix}-city`}
          {...form.register('city' as FieldPath<T>)}
          aria-invalid={errors.city ? true : undefined}
        />
        <FieldError message={errors.city ? copy('platform.form.city') : undefined} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-country`}>Land</Label>
        <Input
          id={`${idPrefix}-country`}
          maxLength={2}
          {...form.register('country' as FieldPath<T>, {
            setValueAs: (value: string) => value.trim().toUpperCase(),
          })}
          aria-invalid={errors.country ? true : undefined}
        />
        <FieldError message={errors.country ? copy('platform.form.country') : undefined} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-from`}>Gültig ab</Label>
        <Input
          id={`${idPrefix}-from`}
          placeholder="JJJJ-MM-TT"
          {...form.register(dateField)}
          aria-invalid={dateError ? true : undefined}
        />
        <FieldError message={dateError ? copy('platform.form.date') : undefined} />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={pending}>
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            Abbrechen
          </Button>
        ) : null}
      </div>
    </form>
  );
}
function ContactCreateForm({ partnerId }: { partnerId: string }) {
  const create = useAddBusinessPartnerContact(partnerId);
  const form = useForm({
    resolver: zodResolver(createBusinessPartnerContactRequestSchema),
    defaultValues: {
      name: '',
      email: null,
      phone: null,
      functionTitle: null,
      isPrimary: false,
      validFrom: '',
      validTo: null,
    },
  });

  return (
    <ContactFields
      form={form}
      idPrefix="contact-new"
      submitLabel="Ansprechpartner anlegen"
      pending={create.isPending}
      dateField="validFrom"
      onValid={async (values) => {
        try {
          await create.mutateAsync({ ...values, isPrimary: values.isPrimary ?? false });
          toast.success('Ansprechpartner angelegt.');
          form.reset();
        } catch (error) {
          toastFailure(error, 'Der Ansprechpartner konnte nicht angelegt werden.');
        }
      }}
    />
  );
}

function ContactSuccessorForm({
  partnerId,
  contact,
  onDone,
}: {
  partnerId: string;
  contact: BusinessPartnerDetail['contacts'][number];
  onDone: () => void;
}) {
  const patch = usePatchBusinessPartnerContact(partnerId);
  const form = useForm({
    resolver: zodResolver(contactSuccessorSchema),
    defaultValues: {
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      functionTitle: contact.functionTitle,
      isPrimary: contact.isPrimary,
      effectiveFrom: '',
    },
  });

  return (
    <ContactFields
      form={form}
      idPrefix={`contact-${contact.id}`}
      submitLabel="Ansprechpartner fortschreiben"
      pending={patch.isPending}
      dateField="effectiveFrom"
      onCancel={onDone}
      onValid={async (values) => {
        try {
          await patch.mutateAsync({
            contactId: contact.id,
            body: { ...values, isPrimary: values.isPrimary ?? contact.isPrimary },
          });
          toast.success('Ansprechpartner fortgeschrieben.');
          onDone();
        } catch (error) {
          toastFailure(error, 'Der Ansprechpartner konnte nicht fortgeschrieben werden.');
        }
      }}
    />
  );
}

function ContactFields<T extends FieldValues>({
  form,
  idPrefix,
  submitLabel,
  pending,
  onValid,
  onCancel,
  dateField,
}: {
  form: UseFormReturn<T>;
  idPrefix: string;
  submitLabel: string;
  pending: boolean;
  onValid: (values: T) => Promise<void>;
  onCancel?: () => void;
  dateField: FieldPath<T>;
}) {
  const copy = useVoiceCopy();
  const errors = form.formState.errors;
  const dateError = errors[dateField];

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-name`}>Name</Label>
        <Input
          id={`${idPrefix}-name`}
          {...form.register('name' as FieldPath<T>)}
          aria-invalid={errors.name ? true : undefined}
        />
        <FieldError message={errors.name ? copy('platform.form.name') : undefined} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-phone`}>Telefon</Label>
        <Input
          id={`${idPrefix}-phone`}
          {...form.register('phone' as FieldPath<T>, { setValueAs: blankToNull })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-email`}>E-Mail</Label>
        <Input
          id={`${idPrefix}-email`}
          {...form.register('email' as FieldPath<T>, { setValueAs: blankToNull })}
          aria-invalid={errors.email ? true : undefined}
        />
        <FieldError message={errors.email ? copy('platform.form.email') : undefined} />
      </div>
      <div className="flex items-center gap-2">
        <input
          id={`${idPrefix}-primary`}
          type="checkbox"
          {...form.register('isPrimary' as FieldPath<T>)}
        />
        <Label htmlFor={`${idPrefix}-primary`}>Primärkontakt</Label>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-from`}>Gültig ab</Label>
        <Input
          id={`${idPrefix}-from`}
          placeholder="JJJJ-MM-TT"
          {...form.register(dateField)}
          aria-invalid={dateError ? true : undefined}
        />
        <FieldError message={dateError ? copy('platform.form.date') : undefined} />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={pending}>
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            Abbrechen
          </Button>
        ) : null}
      </div>
    </form>
  );
}
